
import { throwError as observableThrowError, Subject, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { RestApi } from '../../../auth/rest.api';
import { BzAlertService } from './../../../shared/components/alert/alert.service';
import { EBzAlertTypes } from './../../../shared/components/alert/core/models/eAlertTypes.enum';
import { saveFile } from 'app/app.module';

declare global {
  interface Navigator {
    msSaveOrOpenBlob: (blobOrBase64: Blob | string, filename: string) => void;
  }
}

@Injectable()
export class TraceService {

  private api_traceConfiguration = 'api/TracingConfiguration/GetTracingConfiguration';
  private file_entries = 'api/Trace/FileEntries';
  private api_trace_save = 'api/TracingConfiguration/SaveTracingConfiguration';
  private file_content = 'api/Trace/';
  private api_file_names = 'api/Trace/FileNames';
  private api_file_details = 'api/Trace/TraceDetail';
  private api_delete_traces = 'api/Trace/Delete';
  private api_download_trace_file = 'api/Trace/Download';
  private api_download_trace_batch_file = 'api/Trace/BatchDownload';
  private api_get_categories = 'api/Trace/Categories';
  public gettingIntoTracing$: Subject<void> = new Subject<void>();

  constructor(
    private _authHttp: RestApi,
    private _bzAlertService: BzAlertService
  ) {
  }

  public removeTraceFiles(traceFiles: any) {
    return this._authHttp.post(this.api_delete_traces, traceFiles);
  }

  public getFileNames(filterCriteria: ITraceFilter): any {
    return this._authHttp.post(this.api_file_names, filterCriteria);
  }

  public getFileDetails(filterCriteria: ITraceFilter): any {
    return this._authHttp.post(this.api_file_details, filterCriteria);
  }

  public getTraceConfiguration() {
    return this._authHttp.get(this.api_traceConfiguration);
  };

  public saveTraceConfiguration(json: any) {
    return this._authHttp.post(this.api_trace_save, json)
      .map(data => data)
      ._catch(this._handleError.bind(this));
  };

  public getEntries() {
    return this._authHttp.get(this.file_entries);
  };

  public getCategories() {
    return this._authHttp.get(this.api_get_categories);
  };

  public getContentEntry(contentName: string) {
    return this._authHttp.get(`${this.file_content}?fileName=${contentName}`);
  }

  public downloadTraceFile(traceFile : IBATraceFile) {
    return this._authHttp.requestBlob({ url: `${this.api_download_trace_file}?name=${traceFile.Name}&category=${traceFile.Category ? traceFile.Category : 'Uncategorized'}`, parameters: [], body: {} }, 'GET')
    .map(result => {
      const fileName: string = this._authHttp.getFileNameFromHeaders(result.headers, traceFile.Name + '.csv');
        if (navigator.appVersion.toString().indexOf('.NET') > 0) {
          saveFile(new Blob([result.body], { type: 'application/octet-stream' }), fileName);
        }
        else {
          const pdfUrl = (window.URL || window['webkitURL']).createObjectURL(new Blob([result.body], { type: 'application/octet-stream' }));
          const anchor = document.createElement('a');
          anchor.download = fileName;
          anchor.href = pdfUrl;
          anchor.click();
        }
    })
    .catch((err) => Observable.throw(err._body));
  }

  public downloadBatchTraceFile(traceFiles : Array<any>) {
    return this._authHttp.requestBlob(
      { url: `${this.api_download_trace_batch_file}`,
      parameters: [],
      body: traceFiles }, 'POST')
    .map(result => {
      const fileName: string = this._authHttp.getFileNameFromHeaders(result.headers, traceFiles[0].name + '.csv');
        if (navigator.appVersion.toString().indexOf('.NET') > 0) {
          saveFile(new Blob([result.body], { type: 'application/octet-stream' }), fileName);
        }
        else {
          const pdfUrl = (window.URL || window['webkitURL']).createObjectURL(new Blob([result.body], { type: 'application/octet-stream' }));
          const anchor = document.createElement('a');
          anchor.download = fileName;
          anchor.href = pdfUrl;
          anchor.click();
        }
    })
    .catch((err) => Observable.throw(err._body));
  }

  private _handleError(error: any) {
    const errMsg = (error._body) ? error._body :
      error.status ? `${error.status} - ${error.statusText}` : 'Server error';

    this._bzAlertService.dispatchMessage({
      message: errMsg,
      type: EBzAlertTypes[EBzAlertTypes.Error]
    } as IBzAlertMessage);

    return observableThrowError(errMsg);
  }
}
